"use client"

import type React from "react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { Search, ShoppingCart, Heart, User, Menu, X, LogOut, Settings, Package } from "lucide-react"
import type { RootState } from "../../store/store"
import { logout } from "../../store/slices/authSlice"
import SearchBar from "../common/SearchBar"
import toast from "react-hot-toast"

const Navbar: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const { totalItems = 0 } = useSelector((state: RootState) => state.cart)
  const { items: wishlistItems = [] } = useSelector((state: RootState) => state.wishlist)

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const handleLogout = () => {
    dispatch(logout())
    setIsUserMenuOpen(false)
    navigate("/")
    toast.success("Logged out successfully")
  }

  const categories = [
    { name: "Beds", path: "/categories/beds" },
    { name: "Sofas", path: "/categories/sofas" },
    { name: "Dining", path: "/categories/dining" },
    { name: "Wardrobes", path: "/categories/wardrobes" },
    { name: "Chairs", path: "/categories/chairs" },
    { name: "Tables", path: "/categories/tables" },
  ]

  return (
    <nav className="bg-primary-900 shadow-lg sticky top-0 z-50">
      {/* Top Bar */}
      <div className="bg-primary-800 text-accent-400 py-2">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-4">
              <span>📞 +91 98765 43210</span>
              <span>✉️ info@anmolfurniture.com</span>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <span>Free Shipping on Orders Above ₹10,000</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-accent-400 rounded-lg flex items-center justify-center">
              <span className="text-primary-900 font-bold text-xl">A</span>
            </div>
            <div className="hidden md:block">
              <h1 className="text-xl font-bold text-accent-400">Anmol Furniture</h1>
              <p className="text-xs text-accent-200">Bazaar</p>
            </div>
          </Link>

          {/* Desktop Search */}
          <div className="hidden lg:block flex-1 max-w-2xl mx-8">
            <SearchBar />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {/* Search Icon for Mobile */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="lg:hidden p-2 text-accent-400 hover:text-accent-300"
            >
              <Search className="w-6 h-6" />
            </button>

            {/* Wishlist */}
            <Link to="/wishlist" className="relative p-2 text-accent-400 hover:text-accent-300 transition-colors">
              <Heart className="w-6 h-6" />
              {wishlistItems?.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {wishlistItems.length}
                </span>
              )}
            </Link>

            {/* Cart */}
            <Link to="/cart" className="relative p-2 text-accent-400 hover:text-accent-300 transition-colors">
              <ShoppingCart className="w-6 h-6" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-2 text-accent-400 hover:text-accent-300 transition-colors"
                >
                  <User className="w-6 h-6" />
                  <span className="hidden xl:block">{user.name}</span>
                </button>

                {/* User Dropdown */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <Link
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    <Link
                      to="/orders"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      <Package className="w-4 h-4" />
                      <span>My Orders</span>
                    </Link>
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-100"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Admin Panel</span>
                      </Link>
                    )}
                    <hr className="my-2" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link to="/login" className="text-accent-400 hover:text-accent-300 transition-colors">
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="bg-accent-400 text-primary-900 px-4 py-2 rounded-lg hover:bg-accent-300 transition-colors font-bold"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-accent-400 hover:text-accent-300"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Categories Bar */}
        <div className="hidden lg:flex items-center space-x-8 py-3 border-t border-primary-800">
          {categories.map((category) => (
            <Link
              key={category.name}
              to={category.path}
              className="text-accent-400 hover:text-accent-200 transition-colors font-medium"
            >
              {category.name}
            </Link>
          ))}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200">
          <div className="px-4 py-4 space-y-4">
            {/* Mobile Search */}
            <div className="mb-4">
              <SearchBar />
            </div>

            {/* Categories */}
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-900">Categories</h3>
              {categories.map((category) => (
                <Link
                  key={category.name}
                  to={category.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-2 text-gray-600 hover:text-primary-600"
                >
                  {category.name}
                </Link>
              ))}
            </div>

            {/* Mobile User Menu */}
            {user ? (
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <Link
                  to="/profile"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-2 py-2 text-gray-600"
                >
                  <Settings className="w-4 h-4" />
                  <span>Profile</span>
                </Link>
                <Link
                  to="/orders"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-2 py-2 text-gray-600"
                >
                  <Package className="w-4 h-4" />
                  <span>My Orders</span>
                </Link>
                <Link
                  to="/wishlist"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-2 py-2 text-gray-600"
                >
                  <Heart className="w-4 h-4" />
                  <span>Wishlist ({wishlistItems?.length || 0})</span>
                </Link>
                <Link
                  to="/cart"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center space-x-2 py-2 text-gray-600"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span>Cart ({totalItems})</span>
                </Link>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-2 py-2 text-gray-600"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Admin Panel</span>
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout()
                    setIsMobileMenuOpen(false)
                  }}
                  className="flex items-center space-x-2 py-2 text-red-600 w-full text-left"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 text-gray-600">
                  Login
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-2 bg-primary-600 text-white text-center rounded-lg"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden">
          <div className="bg-white p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Search Products</h3>
              <button onClick={() => setIsSearchOpen(false)} className="p-2 text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <SearchBar onSearch={() => setIsSearchOpen(false)} />
          </div>
        </div>
      )}

      {/* Click outside to close user menu */}
      {isUserMenuOpen && <div className="fixed inset-0 z-40" onClick={() => setIsUserMenuOpen(false)} />}
    </nav>
  )
}

export default Navbar
